import * as styles from './FileMoveDialog.module.less';
import { useEffect, useState } from 'react';
import { IMoveFilesCmd } from '@/twapp/server/FileEndpoint';
import { createMoveCmd, getMovableEntries, IMovableEntries, IPreparedMove } from './fileMove';
import { TromanDialog, TromanDialogBody, TromanDialogBodySection, TromanDialogFooter, TromanDialogTitleHeader } from '../../TromanDialog';
import { ButtonVariants, Icon, Icons, TromanButton } from '../../Buttons';
import { IContainer, IFileRoot, IFolder } from '../model/fileModel';
import { FileManagerCrumbs } from '../FileManagerCrumbs';
import { WithOptional } from '@/common/typeHelpers';

export interface IFileMoveDialogProps {
    root: IFileRoot,
    files: IPreparedMove;
    busy: boolean;
    onClose: (cmd?: IMoveFilesCmd) => void;
}

const FileMoveInner = ({files, root, busy, onClose}: IFileMoveDialogProps) => {
    const [current, setCurrent] = useState<IContainer>(root);
    const [path, setPath] = useState<IFolder[]>([]);
    const [rename, setRename] = useState(false);
    const [movable, setMovable] = useState<IMovableEntries>();

    useEffect(() => {
        setCurrent(root);
        setPath([]);
        setRename(false);
        setMovable(getMovableEntries(root, files))
    }, [root, files])
    
    const navTo = (path: IFolder[]) => {
        const next = path.length === 0 ? root : path[path.length - 1];
        setPath(path);
        setCurrent(next);
        setRename(false);
        setMovable(getMovableEntries(next, files));
    }

    return (
        <>
            <TromanDialogTitleHeader title='Flytta dokument'/>
            <TromanDialogBody>
                <TromanDialogBodySection>
                    <div className={styles.title}>Välj mapp att flytta till</div>
                    <div className={styles.foldersContainer}>
                        <div className={styles.crumbsContainer}>
                            <FileManagerCrumbs path={path} setPath={navTo}/>
                        </div>
                        <div>
                            {
                                current.folders.length > 0 ? current.folders.map(x => (
                                    <div key={x.id}
                                        className={files.invalidTargetsIds.has(x.id) ? styles.disabledFolder : styles.folder} 
                                        onDoubleClick={() => { !files.invalidTargetsIds.has(x.id) && navTo([...path, x]); }}>
                                        <div className={styles.icon}><Icon icon={Icons.Folder}/></div>
                                        <div className={styles.name}>{x.name}</div>
                                    </div>
                                )) : (
                                    <div className={styles.emptyMessage}>Mappen innehåller inga undermappar</div>
                                )
                            }
                        </div>
                    </div>
                </TromanDialogBodySection>
                {
                    movable && movable.needrename.length > 0 && (
                        <TromanDialogBodySection>
                            <div className={styles.renameHeader}>
                            <div className={styles.title}>Existerande dokument</div>
                                <div>Följande dokument finns redan i mappen och kan inte flyttas</div>
                                <div className='checkbox'>
                                    <label>
                                        <input type='checkbox' checked={rename} onChange={x => setRename(x.target.checked)}/>
                                        Byt namn på dokumenten och flytta
                                    </label>
                                </div>
                            </div>
                            <div className={styles.renameContainer}>
                                {
                                    movable.needrename.map(x => (<div key={x.moving.id}>{x.moving.name}</div>))
                                }
                            </div>
                        </TromanDialogBodySection>
                    )
                }
            </TromanDialogBody>
            <TromanDialogFooter>
                <TromanButton text='Flytta'
                              variant={ButtonVariants.Primary}
                              disabled={busy || !movable || (movable.noconflict.length === 0 && (!rename || movable.needrename.length === 0))}
                              onClick={() => movable && onClose(createMoveCmd(root, current, movable, rename))}/>
                <TromanButton text='Avbryt' variant={ButtonVariants.Default} disabled={busy} onClick={() => onClose()}/>
            </TromanDialogFooter>
        </>
    )
};

export const FileMoveDialog = ({files, ...props}: WithOptional<IFileMoveDialogProps, "files">) => {
    return (
        <TromanDialog open={!!files} onClose={() => props.onClose()}>
            { files && (<FileMoveInner files={files} {...props}/>) }
        </TromanDialog>
    );
};